import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Grid, Link, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import enGB from 'date-fns/locale/en-GB'
import enDE from 'date-fns/locale/de'
import { useNavigate } from 'react-router-dom'
import { HelpOutlineRounded } from '@mui/icons-material'
import PtUpload from '../../components/fs-file-upload/fs-pt-upload'
import { ReactComponent as UploadAssistant } from '../../assets/Upload_first.svg'
import { ReactComponent as UploadSuccessful } from '../../assets/Upload_success.svg'
import { getIsDemoAccount } from '../../utils'
import FsDemoAccountModal from '../../components/common/molecules/DemoAccountModal'
import { ptAPI } from '../../shared/api/PTAPI'
import { getIcon } from '../../components/common/Icons'
import FsFullPageLoader from '../../components/common/atoms/FullPageLoader'
import { useAppSelector } from '../../store/hooks'

const PtUploadView = () => {
  const { t, i18n } = useTranslation()
  const [startValue, setStartValue] = useState<Date | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showUpload, setShowUpload] = useState<boolean>(false)
  const [openDemoModal, setOpenDemoModal] = useState<boolean>(false)
  const [onSuccess, setOnSuccess] = useState<boolean>(false)
  const navigate = useNavigate()
  const { currentLanguage } = useAppSelector((state) => state.currentUser)

  const theme = useTheme()

  const defaultLocale = i18n.language === 'en' ? enGB : enDE
  const isDemoAccount = getIsDemoAccount()

  const restart = () => {
    setOnSuccess(false)
    setStartValue(null)
  }

  const checkFileUploadStatus = async () => {
    try {
      setIsLoading(true)
      const filesExist = await ptAPI.getFileUploadStatus()
      if (filesExist.status) {
        setOnSuccess(true)
      }
    } catch {
      // Nothing to do if this fails
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    checkFileUploadStatus()
  }, [])

  return (
    <>
      <FsDemoAccountModal isOpen={openDemoModal} setIsOpen={setOpenDemoModal} app="pt" />
      <Stack direction="row" alignItems="center" mb={2}>
        <Box mt={1}>{getIcon('etl')}</Box>
        <Typography variant="h1" ml={1} pr={1} mt={0.5}>
          {t('upload.data')}
        </Typography>
        <Tooltip
          title={
            <Box>
              <Typography textAlign="center">
                {i18n.language === 'en'
                  ? 'More questions? Please contact us at:'
                  : 'Noch Fragen? Wir helfen gern unter:'}{' '}
                <Link href="mailto:support.pt@ines-analytics.com">
                  support.pt@ines-analytics.com
                </Link>
              </Typography>
            </Box>
          }
        >
          <HelpOutlineRounded sx={{ fontSize: 26, color: theme.palette.text.secondary, mt: 0.25 }} />
        </Tooltip>
      </Stack>
      <Box
        sx={{
          height: 'calc(100vh - 139px)',
          maxWidth: '1000px',
          textAlign: 'justify',
          overflowY: 'auto',
          pb: 6,
          pr: 2,
        }}
      >
        {onSuccess ? (
          <Box>
            <Typography variant="h6">
              {currentLanguage === 'de' ? 'Datei erhalten' : 'File received'}
            </Typography>
            <Typography mb={4}>
              {t('pt.data.received', {
                year: startValue?.getFullYear(),
              })}
            </Typography>
            <Stack alignItems="center">
              <UploadSuccessful />
              <Typography fontWeight="bold" mt={4}>
                {t('pt.new.date')}
              </Typography>
              <Button variant="contained" onClick={restart} sx={{ mt: 2 }}>
                {t('new.date.action')}
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <UploadAssistant />
              <Box>
                <Typography mt={4}>{t('upload.intro.PT')}</Typography>
                <Typography variant="h6" mt={4}>
                  {currentLanguage === 'de' ? 'Erforderliche Daten' : 'Data needed'}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                backgroundColor: theme.palette.secondary.light,
                p: 2,
                borderRadius: 2,
                mt: 1,
                width: '100%',
              }}
            >
              <Typography sx={{ whiteSpace: 'pre' }}>{t('upload.intro.PT.desc')}</Typography>
              <Typography mt={2} mb={1} fontWeight="bold">
                {i18n.language === 'en'
                  ? 'Download our template to get started!'
                  : 'Laden Sie unsere Vorlage herunter, um loszulegen!'}
              </Typography>
              <Button
                href={`${process.env.REACT_APP_URL}/static/PT_data_upload_template.csv`}
                variant="contained"
                sx={{ display: 'block', width: 'fit-content', mb: 1 }}
              >
                {t('download.file')}
              </Button>

              <Link onClick={() => navigate('/pt/data-upload-steps')} sx={{ cursor: 'pointer' }}>
                {i18n.language === 'en'
                  ? 'Learn more about the template.'
                  : 'Erfahren Sie mehr über die Vorlage.'}
              </Link>
            </Box>
            <Box>
              <Typography variant="h6" mt={4}>
                {currentLanguage === 'de' ? 'Jahr' : 'Year'}
              </Typography>
              <Typography mb={2} mt={1}>
                {t('select.upload.date')}
              </Typography>
            </Box>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={defaultLocale}>
              <DatePicker
                disableFuture
                label={`${t('data.from')} *`}
                views={['year']}
                value={startValue}
                maxDate={new Date()}
                onChange={(newValue: any) => {
                  if (isDemoAccount) {
                    setOpenDemoModal(true)
                    return
                  }
                  if (newValue) {
                    setStartValue(newValue)
                    setShowUpload(true)
                  }
                }}
                slotProps={{
                  field: {
                    readOnly: true,
                  },
                }}
              />
            </LocalizationProvider>
            {showUpload ? (
              <Grid container my={4}>
                <Grid size={{ xs: 12 }}>
                  <PtUpload
                    date={startValue}
                    setIsLoading={setIsLoading}
                    onSuccess={() => setOnSuccess(true)}
                  />
                </Grid>
              </Grid>
            ) : null}
            <Typography textAlign="center" mt={4}>
              {i18n.language === 'en'
                ? 'More questions? Please contact us at:'
                : 'Noch Fragen? Wir helfen gern unter:'}{' '}
              <Link href="mailto:support.pt@ines-analytics.com">support.pt@ines-analytics.com</Link>
            </Typography>
          </Box>
        )}
      </Box>

      {isLoading && <FsFullPageLoader />}
    </>
  )
}

export default PtUploadView
